<template>
  <div>
    <button
      id="layers-button"
      data-tool-tip-target="layers-mode-button"
      data-test="layers-button"
      :disabled="disabled"
      @click="toggleMainCard"
    >
      <ys-icon icon="ysLayers" size="medium" class="layers-icon" />
    </button>
    <animation-wrap :type="ANIMATION_TYPES.FADE_TRANSLATE">
      <x-card-layout v-if="cardOpen" id="layers-card" full-height>
        <ys-card
          :headline="cardTitle"
          :back-button="cardOpen === CARD_LAYERS_MAIN ? undefined : true"
          @close="closeCard"
          @back="goBackToMain"
        >
          <main-card
            v-if="cardOpen === CARD_LAYERS_MAIN"
            data-test="main-card"
            @open-layer-details="openLayerDetail"
            @open-legend="openLegend"
          />
          <layer-detail
            v-else-if="cardOpen === CARD_LAYERS_DETAIL && layer"
            :id="layer.id"
            :label="layer.label"
            :full-img="layer.fullImg"
            :thumb-img="layer.img"
            :component="layer.component"
            :has-access="layer.hasAccess"
            data-test="layer-detail-card"
            class="layer-detail"
          />
          <div
            v-else-if="cardOpen === CARD_LAYERS_LEGEND"
            ref="legendContainer"
            class="legend-content"
            data-test="legend-card"
          >
            <fish-legend-tab />
          </div>
        </ys-card>
      </x-card-layout>
    </animation-wrap>
  </div>
</template>
<script setup lang="ts">
///// imports /////
import { ref, computed, watch, watchEffect } from 'vue'
import { useRoute } from 'vue-router'
import '@onxmaps/yellowstone-ui/ys-card'
import '@onxmaps/yellowstone-ui/ys-icon'
import '@onxmaps/yellowstone-ui/icons/ysLayers'

import { ANIMATION_TYPES } from '@/lib/enums'
import { getMode } from '@/lib/modes'
import { EVENTS, LEGEND_ORIGINS, BASEMAP_ORIGINS } from '@/lib/enums-marketing'

import animationWrap from '@/components/shared/animation-wrap.vue'
import { XCardLayout } from '@onxmaps/vue-components'
import mainCard from '@/fish/components/map-controls/control-layers/main-card.vue'
import layerDetail from '@/fish/components/map-controls/control-layers/layer-detail.vue'
import fishLegendTab from '@/fish/components/layers/legend-tab.vue'

import marketingEventService from '@/services/marketing-event-service'
import { viewerDimension, mapReadyPromise } from '@/services/map-service'
import { basemapMarketingName } from '@/services/basemap-service'
import layerService from '@/services/layer-service'
import pubsub from '@/lib/pubsub'
import {
  useCardsStore,
  CARD_LAYERS_MAIN,
  CARD_LAYERS_LEGEND,
  CARD_LAYERS_DETAIL,
  type CardId,
} from '@/stores/cards-store'
import { resolveLayerById } from '@/fish/components/map-controls/control-layers/deep-link'

///// props/emits /////
defineProps({
  disabled: { type: Boolean, required: true },
})

///// refs and variables /////
const route = useRoute()

const cardsStore = useCardsStore()

const legendTimerStop = ref()
const legendTimerStart = ref()
const didScroll = ref(false)
const addScrollWatch = ref(false)
const legendContainer = ref<HTMLDivElement | null>(null)

// FUTURE - update TS here to use interface from activity modes/layer service when one is defined
const layer = ref<null | any>(null)

///// computed /////
const mapMode = computed(getMode)
const cardTitle = computed(() => {
  return legendContainer.value ? 'Map Legend' : 'Map Layers'
})

const cardOpen = computed<null | CardId>(() => {
  if (cardsStore.isCardActive(CARD_LAYERS_DETAIL)) {
    return CARD_LAYERS_DETAIL
  }
  if (cardsStore.isCardActive(CARD_LAYERS_LEGEND)) {
    return CARD_LAYERS_LEGEND
  }
  if (cardsStore.isCardActive(CARD_LAYERS_MAIN)) {
    return CARD_LAYERS_MAIN
  }
  return null
})

///// functions /////
const publishBasemapMenuOpened = () => {
  let origin
  if (route.query?.origin === 'welcome') {
    origin = { origin: BASEMAP_ORIGINS.WELCOME }
  }
  marketingEventService.publish('basemap_menu_opened', {
    active_basemap: basemapMarketingName.value,
    map_viewer_dimension: viewerDimension(),
    ...origin,
  })
}

// Activates exactly one of the three cards (or none, if id is null),
// clearing the others so only a single sub-view is ever shown at once.
const showOnlyCard = (id: CardId | null, param?: string) => {
  const cards = [CARD_LAYERS_MAIN, CARD_LAYERS_LEGEND, CARD_LAYERS_DETAIL]
  cards.forEach((cardId) => {
    cardsStore.setCardState(
      cardId,
      cardId === id,
      cardId === id ? param : undefined
    )
  })
}

const toggleMainCard = async () => {
  await mapReadyPromise
  if (cardsStore.isCardActive(CARD_LAYERS_MAIN)) {
    cardsStore.setCardState(CARD_LAYERS_MAIN, false)
  } else {
    showOnlyCard(CARD_LAYERS_MAIN)
    publishBasemapMenuOpened()
  }
}

const openLayerDetail = (layerDetails: any) => {
  marketingEventService.publish('layer_details_opened', {
    layer_name: layerDetails.label,
    map_mode: mapMode.value,
  })
  layer.value = layerDetails
  showOnlyCard(CARD_LAYERS_DETAIL, layerDetails.id)
}

const openLegend = () => {
  showOnlyCard(CARD_LAYERS_LEGEND)
}

const goBackToMain = () => {
  showOnlyCard(CARD_LAYERS_MAIN)
}

const closeCard = () => {
  showOnlyCard(null)
}

const handleScroll = () => {
  didScroll.value = true
  legendContainer.value?.removeEventListener('scroll', handleScroll)
}

///// watchers /////
watchEffect(async () => {
  await mapReadyPromise
  await layerService.layersReadyPromise

  const layerId = cardsStore.getCardParam(CARD_LAYERS_DETAIL)
  if (!cardsStore.isCardActive(CARD_LAYERS_DETAIL) || !layerId) {
    return
  }
  if (layer.value?.id === layerId) {
    return
  }

  const resolved = resolveLayerById(layerId, mapMode.value)
  if (!resolved) {
    cardsStore.setCardState(CARD_LAYERS_DETAIL, false)
    return
  }
  layer.value = resolved
})

// eslint-disable-next-line require-await
watch(cardOpen, (newCard, oldCard) => {
  if (newCard === CARD_LAYERS_LEGEND) {
    legendTimerStart.value = performance.now()
    didScroll.value = false
    addScrollWatch.value = true
  }
  if (oldCard === CARD_LAYERS_LEGEND) {
    legendTimerStop.value = performance.now()

    marketingEventService.publish(EVENTS.MAP_LEGEND, {
      name: EVENTS.MAP_LEGEND,
      origin: LEGEND_ORIGINS.BASEMAP,
      map_viewer_dimension: viewerDimension(),
      active_basemap: basemapMarketingName.value,
      did_scroll: didScroll.value,
      dismissal_time: Math.round(
        (legendTimerStop.value - legendTimerStart.value) / 1000
      ),
    })
  }

  // Publish the card change state, so that other UI may react to it as needed
  pubsub.publish('layerCardToggled', { active: !!cardOpen.value })
})

watchEffect(() => {
  if (addScrollWatch.value && legendContainer.value) {
    legendContainer.value?.addEventListener('scroll', handleScroll)
    addScrollWatch.value = false
  }
})
</script>
<style lang="scss" scoped>
@use '@/styles/system/_shadow.scss';
@use '@/styles/system/_colors.scss';
@use '@/styles/system/_breakpoints.scss';

#layers-button {
  width: 45px;
  height: 45px;
  box-shadow: shadow.$Low;
  background-color: var(--ys-color-background-primary);
  color: var(--ys-color-icon-primary);
  border-radius: var(--ys-radius-100);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  .layers-icon {
    color: var(--ys-color-icon-primary);
  }

  @include breakpoints.app-prototyping() {
    color: var(--ys-color-text-reverse);
    background-color: var(--ys-color-surface-reverse);

    :deep(.x-icon) {
      svg {
        fill: var(--ys-color-text-reverse);
      }
    }
  }
}

.layer-detail.content {
  background-color: var(--ys-color-background-primary);
  height: 100%;
}

#layers-card {
  position: fixed;
  left: calc(100vw - 455px);
  top: 20px;
  max-height: calc(100% - 50px);
  overflow: hidden;

  ys-card {
    background-color: var(--ys-color-background-primary);
    .content {
      background-color: var(--ys-color-background-primary);
    }
  }

  @include breakpoints.mobile() {
    // Recreate some mobile styles from "card" component
    left: 15px;
    top: 75px;
    max-height: calc(
      100% -
        (
          calc(var(--card-top-offset) + var(--card-banner-offset)) +
            var(--card-bottom-offset)
        )
    );
  }

  @include breakpoints.app-prototyping {
    left: 0;
    top: auto;
    bottom: 0;
    height: 400px;
    width: 100vw;

    &.mobileExpanded {
      height: 100%;
    }
  }
}

.legend-content {
  background-color: var(--ys-color-background-primary);
}
</style>
